'use client';

/**
 * useCheckoutDelivery Hook
 *
 * Focused hook for delivery mode operations via OrderApp.
 *
 * The OrderApp backend defines two delivery modes: HOME_DELIVERY and
 * BOUTIQUE_PICKUP.  These are **not** the same as Saleor shipping methods.
 * The caller decides the mode and passes it directly — no heuristic
 * mapping from Saleor method IDs is needed.
 */

import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { checkoutDeliveryMethodUpdate } from '@/lib/checkout/operations';
import { localizeApiErrors } from '@/lib/checkout/errors/localizeApiErrors';
import type { CheckoutAddressPayload } from '@/lib/checkout/operations/types';
import type { OrderAppDeliveryMode } from '@/graphql/orderApp/api';
import type { ApiError } from '@/lib/checkout/types';
import { checkoutActions, checkoutState } from '@/states/checkout';

/**
 * Map API errors to simple format
 */
function mapErrors(errors: ApiError[]): Array<{ code: string; message: string; field?: string }> {
  return errors.map((e) => ({
    code: e.code || 'UNKNOWN',
    message: e.message || 'An error occurred',
    field: e.field || undefined,
  }));
}

/**
 * Shipping method type (Saleor shipping methods — used for display only)
 */
export interface ShippingMethod {
  id: string;
  name: string;
  description?: string;
  price: {
    amount: number;
    currency: string;
  };
  minimumDeliveryDays?: number;
  maximumDeliveryDays?: number;
}

/**
 * Delivery operation result
 */
export interface DeliveryOperationResult {
  success: boolean;
  errors: Array<{ code: string; message: string; field?: string }>;
}

/**
 * Hook options
 */
export interface UseCheckoutDeliveryOptions {
  locale?: string;
}

/**
 * Focused hook for delivery mode operations.
 *
 * Primary API: `updateDeliveryMode(mode, options)` — sends the OrderApp
 * delivery mode directly without any ID heuristics.
 */
export function useCheckoutDelivery(options: UseCheckoutDeliveryOptions = {}) {
  const snapshot = useSnapshot(checkoutState);

  // Parse locale
  const locale = options.locale || 'us-en';
  const [, languageSlug = 'en'] = locale.split('-');

  /**
   * Available shipping methods from Saleor checkout (for display/pricing)
   */
  const availableMethods = useMemo((): ShippingMethod[] => {
    const methods = snapshot.checkout?.shippingMethods || [];
    return methods.map((m) => ({
      id: m.id,
      name: m.name,
      description: m.description ?? undefined,
      price: {
        amount: m.price?.amount || 0,
        currency: m.price?.currency || 'USD',
      },
      minimumDeliveryDays: m.minimumDeliveryDays ?? undefined,
      maximumDeliveryDays: m.maximumDeliveryDays ?? undefined,
    }));
  }, [snapshot.checkout?.shippingMethods]);

  /**
   * Currently selected Saleor shipping method (for display)
   */
  const selectedMethod = useMemo((): ShippingMethod | null => {
    const deliveryMethod = snapshot.checkout?.deliveryMethod;
    if (!deliveryMethod || deliveryMethod.__typename !== 'ShippingMethod') {
      return null;
    }

    return availableMethods.find((m) => m.id === deliveryMethod.id) || null;
  }, [snapshot.checkout?.deliveryMethod, availableMethods]);

  /**
   * Update delivery mode via OrderApp.
   *
   * Accepts the OrderApp delivery mode directly (`HOME_DELIVERY` or
   * `BOUTIQUE_PICKUP`) — no heuristic mapping from shipping method IDs.
   *
   * For boutique pickup, the caller should provide `boutiqueExtReference`
   * (Algolia store_id) and a minimal address (name + phone from billing).
   * For home delivery, provide the full shipping address.
   */
  const updateDeliveryMode = useCallback(
    async (
      deliveryMode: OrderAppDeliveryMode,
      modeOptions?: {
        address?: CheckoutAddressPayload;
        boutiqueExtReference?: string;
      }
    ): Promise<DeliveryOperationResult> => {
      if (!checkoutState.id) {
        const localized = localizeApiErrors(
          [{ code: 'NO_CHECKOUT', message: 'No checkout found' }],
          locale
        );
        return {
          success: false,
          errors: mapErrors(localized),
        };
      }

      checkoutActions.setProcessing(true);
      checkoutActions.clearErrors();

      try {
        const { checkout, errors } = await checkoutDeliveryMethodUpdate({
          id: checkoutState.id,
          deliveryMode,
          languageCode: languageSlug.toUpperCase(),
          address: modeOptions?.address,
          boutiqueExtReference: modeOptions?.boutiqueExtReference,
        });

        if (errors?.length) {
          const localizedErrors = localizeApiErrors(errors, locale);
          checkoutActions.setErrors(localizedErrors, 'checkout');
          return { success: false, errors: mapErrors(localizedErrors) };
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
        }
        checkoutActions.setDeliveryMode(deliveryMode, modeOptions?.boutiqueExtReference);

        return { success: true, errors: [] };
      } finally {
        checkoutActions.setProcessing(false);
      }
    },
    [languageSlug, locale]
  );

  /**
   * Check if shipping is required for this checkout
   */
  const isShippingRequired = snapshot.checkout?.isShippingRequired ?? true;

  /**
   * Format delivery estimate string
   */
  const formatDeliveryEstimate = useCallback((method: ShippingMethod): string => {
    if (!method.minimumDeliveryDays && !method.maximumDeliveryDays) {
      return '';
    }

    if (method.minimumDeliveryDays === method.maximumDeliveryDays) {
      return `${method.minimumDeliveryDays} business days`;
    }

    if (method.minimumDeliveryDays && method.maximumDeliveryDays) {
      return `${method.minimumDeliveryDays}-${method.maximumDeliveryDays} business days`;
    }

    if (method.maximumDeliveryDays) {
      return `Up to ${method.maximumDeliveryDays} business days`;
    }

    return `From ${method.minimumDeliveryDays} business days`;
  }, []);

  return {
    // State
    availableMethods,
    selectedMethod,
    selectedMethodId: snapshot.deliveryMethodId,
    isShippingRequired,
    hasAvailableMethods: availableMethods.length > 0,
    hasSelectedMethod: !!selectedMethod,
    isProcessing: snapshot.isProcessing,

    // Operations
    updateDeliveryMode,

    // Utilities
    formatDeliveryEstimate,
  };
}
